import React, { useState, useEffect } from "react";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Stack,
  Divider,
  Chip,
  IconButton,
} from "@mui/material";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";

import axiosInstance from "../utils/api";
import PerformanceCards from "../components/PerformanceCard";
import PerformanceChart from "../components/PerformanceChart";
import PerformanceLeaderboard from "../components/PerformanceLeaderboard";

import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  AreaChart,
  Area,
  ResponsiveContainer,
  Legend,
} from "recharts";

// --------------------------------------------------
// PLACEHOLDER DATA (static fallback visuals)
// --------------------------------------------------
const leadStatus = [
  { name: "Active", value: 60 },
  { name: "Pending", value: 25 },
  { name: "Closed", value: 15 },
];
const COLORS = ["#2e7d32", "#fb8c00", "#9e9e9e"];

const repPerformance = [
  { rep: "Rep A", leads: 35 },
  { rep: "Rep B", leads: 18 },
  { rep: "Rep C", leads: 27 },
  { rep: "Rep D", leads: 10 },
];

const trend = [
  { period: "W1", created: 24, closed: 10 },
  { period: "W2", created: 28, closed: 13 },
  { period: "W3", created: 21, closed: 9 },
  { period: "W4", created: 34, closed: 17 },
  { period: "W5", created: 29, closed: 15 },
];

const notifications = [
  { id: 1, text: "Rep Sarah closed 3 leads in Phoenix." },
  { id: 2, text: "Lead #2341 follow-up due in 2 days." },
  { id: 3, text: "Rep Mike checked in from Dallas territory." },
];

// --------------------------------------------------
// SMALL METRIC CARD (placeholder summary section)
// --------------------------------------------------
const MetricCard = ({ label, value, icon }) => (
  <Card sx={{ height: "100%", boxShadow: 1 }}>
    <CardContent>
      <Stack direction="row" spacing={2} alignItems="center">
        <IconButton size="small" sx={{ bgcolor: "action.hover" }} disabled>
          {icon}
        </IconButton>
        <Box>
          <Typography variant="body2" color="text.secondary">
            {label}
          </Typography>
          <Typography variant="h6" fontWeight={700}>
            {value}
          </Typography>
        </Box>
      </Stack>
    </CardContent>
  </Card>
);

// --------------------------------------------------
// MAIN DASHBOARD COMPONENT
// --------------------------------------------------
export default function DashboardV2() {
  const [summary, setSummary] = useState(null);
  const [series, setSeries] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [sRes, tRes] = await Promise.all([
          axiosInstance.get("/api/performance/summary/"),
          axiosInstance.get("/api/performance/?group_by=day"),
        ]);
        const summaryData = sRes.data.totals || sRes.data.summary;
        setSummary(summaryData);
        setLeaderboard(sRes.data.leaderboard || []);
        setSeries(tRes.data.series || []);
      } catch (err) {
        console.error("Performance fetch error:", err);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  if (loading) return <div className="p-6 text-center">Loading performance data...</div>;

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" fontWeight={700} sx={{ mb: 2 }}>
        Performance Analytics
      </Typography>

      {/* Dynamic Performance from Backend */}
      <PerformanceCards data={summary} />
      <PerformanceChart data={series} />
      <PerformanceLeaderboard data={leaderboard} />

      {/* -------- Additional Static Analytics Grid (optional visuals) -------- */}
      <Grid container spacing={1.5} sx={{ mt: 4, mb: 4 }}>
        <Grid item xs={12} sm={6} md={4} lg={2.4}>
          <MetricCard label="Total Leads" value="150" icon={<TrendingUpIcon />} />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={2.4}>
          <MetricCard label="Active" value="92" icon={<TaskAltIcon />} />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={2.4}>
          <MetricCard label="Closed" value="40" icon={<PeopleAltIcon />} />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={2.4}>
          <MetricCard label="Follow-ups" value="18" icon={<AccessTimeIcon />} />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={2.4}>
          <MetricCard label="Active Reps" value="6" icon={<PeopleAltIcon />} />
        </Grid>
      </Grid>

      {/* 2-column grid below */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
          gap: 3,
        }}
      >
        {/* Lead Status Pie */}
        <Card sx={{ height: 400 }}>
          <CardContent>
            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
              Lead Status Distribution
            </Typography>
            <Box sx={{ height: 320 }}>
              <ResponsiveContainer>
                <PieChart>
                  <Pie
                    data={leadStatus}
                    dataKey="value"
                    nameKey="name"
                    innerRadius="60%"
                    outerRadius="85%"
                    paddingAngle={3}
                  >
                    {leadStatus.map((entry, i) => (
                      <Cell key={entry.name} fill={COLORS[i % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend verticalAlign="bottom" height={28} />
                </PieChart>
              </ResponsiveContainer>
            </Box>
          </CardContent>
        </Card>

        {/* Rep Performance Bar */}
        <Card sx={{ height: 400 }}>
          <CardContent>
            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
              Rep Performance
            </Typography>
            <ResponsiveContainer width="100%" height={320}>
              <BarChart data={repPerformance} layout="vertical" margin={{ left: 32 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" />
                <YAxis type="category" dataKey="rep" width={80} />
                <Tooltip />
                <Bar dataKey="leads" radius={[4, 4, 4, 4]} fill="#1976d2" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Performance Trend */}
        <Card sx={{ height: 400 }}>
          <CardContent>
            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
              Performance Trend
            </Typography>
            <ResponsiveContainer width="100%" height={320}>
              <AreaChart data={trend} margin={{ left: 8, right: 8 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="period" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Area
                  type="monotone"
                  dataKey="created"
                  name="Created"
                  stroke="#2196f3"
                  fill="#2196f3"
                  fillOpacity={0.25}
                />
                <Area
                  type="monotone"
                  dataKey="closed"
                  name="Closed"
                  stroke="#4caf50"
                  fill="#4caf50"
                  fillOpacity={0.15}
                />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Notifications */}
        <Card sx={{ height: 400 }}>
          <CardContent>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
              <NotificationsActiveIcon fontSize="small" />
              <Typography variant="subtitle1" fontWeight={700}>
                Notifications
              </Typography>
            </Stack>
            <Divider sx={{ mb: 1 }} />
            <Stack spacing={1.25} sx={{ height: 320, overflowY: "auto" }}>
              {notifications.map((n) => (
                <Stack direction="row" key={n.id} spacing={1} alignItems="center">
                  <Chip size="small" label="New" color="primary" variant="outlined" />
                  <Typography variant="body2">{n.text}</Typography>
                </Stack>
              ))}
            </Stack>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
}
